/**
 * TC-29: pure helpers for OPTIMISTIC channel-message sending.
 *
 * `useSendChannelMessage` used to write the message into the cache only in
 * `onSuccess`, so the bubble appeared when the POST resolved (100-300ms+).
 * Now `onMutate` appends a temporary row (`tmp-<uuid>`, `pending: true`) via
 * `patchChannelMessagesCache`; `onSuccess` swaps it for the server's canonical
 * row; `onError` removes the tmp row from the query cache and parks the FULL
 * failed row (`pending: false, failed: true`) in the failed-outbox store
 * (failed-outbox.ts) — a failed row living in the refetchable cache would be
 * wiped by any invalidate→refetch before the user can Reintentar. Consumers
 * merge outbox rows into the rendered feed with `mergeFailedIntoFeed`.
 *
 * All cache transforms here are PURE (no React, no query-client) so they can
 * be unit-tested — same pattern as message-order.ts / scroll-stick.ts. They
 * also preserve referential identity on no-ops (`markOptimisticFailed` /
 * `removeOptimisticMessage` return the INPUT array when nothing matched),
 * which `patchChannelMessagesCache` relies on (`next === input` → skip page).
 *
 * Dedupe interplay with the realtime broadcast: the server row also arrives
 * via `message.new` → invalidate → refetch. Two lines of defense:
 * 1. `applyOptimisticReplace` detects the canonical id already in the list
 *    (WS refetch beat the mutation) and just drops the tmp row.
 * 2. `flattenMessagePages` dedupes by id on read, so even a row landing on
 *    two pages renders once.
 */
import type { ChannelMessage } from './queries';

/** Prefix of every client-generated optimistic message id. */
export const OPTIMISTIC_ID_PREFIX = 'tmp-';

/** True for ids minted by `buildOptimisticMessage` (never server ids). */
export function isOptimisticId(id: string | undefined | null): boolean {
  return !!id && id.startsWith(OPTIMISTIC_ID_PREFIX);
}

function randomSuffix(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Ancient-environment fallback — uniqueness is per-session/per-cache only.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export interface OptimisticMessageInput {
  channelId: string;
  content: string;
  /** Defaults to 'text'. */
  messageType?: string;
  /** Media fields — present when the send carries an attachment (the upload
   *  hook funnels through the same mutation). */
  fileUrl?: string;
  fileName?: string;
  fileMimeType?: string;
  fileSize?: number;
  /** Sender identity (from the auth store) so the row renders as "mine". */
  userId?: string;
  userName?: string;
  userAvatar?: string;
  /** Injectable for tests — defaults to a fresh `tmp-<uuid>`. */
  id?: string;
  /** Injectable for tests — defaults to `new Date().toISOString()`. */
  nowIso?: string;
}

/**
 * Build the temporary row appended in `onMutate`. Always `pending: true` and
 * an id under `OPTIMISTIC_ID_PREFIX`; `created_at = now` so `sortMessagesAsc`
 * lands it at the bottom of the feed (by the composer), exactly where the
 * canonical row will render after the swap.
 */
export function buildOptimisticMessage(input: OptimisticMessageInput): ChannelMessage {
  return {
    id: input.id ?? `${OPTIMISTIC_ID_PREFIX}${randomSuffix()}`,
    channel_id: input.channelId,
    user_id: input.userId,
    user_name: input.userName,
    ...(input.userAvatar ? { user_avatar: input.userAvatar } : {}),
    content: input.content,
    message_type: input.messageType ?? 'text',
    created_at: input.nowIso ?? new Date().toISOString(),
    ...(input.fileUrl ? { file_url: input.fileUrl } : {}),
    ...(input.fileName ? { file_name: input.fileName } : {}),
    ...(input.fileMimeType ? { file_mime_type: input.fileMimeType } : {}),
    ...(typeof input.fileSize === 'number' ? { file_size: input.fileSize } : {}),
    pending: true,
  };
}

/**
 * onSuccess transform: replace the tmp row with the server's canonical row.
 *
 * Handles every race ordering:
 * - Canonical id ALREADY in the list (the `message.new`-triggered refetch beat
 *   the mutation callback) → just remove the tmp row, never duplicate.
 * - tmp present, canonical absent → in-place swap (position preserved).
 * - NEITHER present (a refetch replaced the cache and dropped the tmp before
 *   the mutation resolved) → append the canonical row (the pre-TC-29
 *   behavior). Under `patchChannelMessagesCache` the append sticks to the
 *   newest page only.
 */
export function applyOptimisticReplace(
  msgs: ChannelMessage[],
  tmpId: string,
  canonical: ChannelMessage,
): ChannelMessage[] {
  const hasTmp = msgs.some((m) => m.id === tmpId);
  const hasCanonical = !!canonical.id && msgs.some((m) => m.id === canonical.id);
  if (hasCanonical) {
    return hasTmp ? msgs.filter((m) => m.id !== tmpId) : msgs;
  }
  if (hasTmp) {
    return msgs.map((m) => (m.id === tmpId ? canonical : m));
  }
  return [...msgs, canonical];
}

/**
 * Mark a tmp row as failed in place. Clears `pending` so spinners stop.
 * Returns the input array untouched when the id isn't present.
 *
 * NOTE (FIX-1): `useSendChannelMessage.onError` no longer uses this — failed
 * rows now live in the failed-outbox store, not the query cache (a refetch
 * would wipe them). Kept as pure-module API for any cache-local use.
 */
export function markOptimisticFailed(
  msgs: ChannelMessage[],
  tmpId: string,
): ChannelMessage[] {
  if (!msgs.some((m) => m.id === tmpId)) return msgs;
  return msgs.map((m) =>
    m.id === tmpId ? { ...m, pending: false, failed: true } : m,
  );
}

/**
 * Drop an optimistic row (Descartar, or the removal step right before a
 * Reintentar re-fires the mutation with a FRESH tmp row). Returns the input
 * array untouched when the id isn't present.
 */
export function removeOptimisticMessage(
  msgs: ChannelMessage[],
  tmpId: string,
): ChannelMessage[] {
  if (!msgs.some((m) => m.id === tmpId)) return msgs;
  return msgs.filter((m) => m.id !== tmpId);
}

/** created_at → epoch ms; unparseable/missing sorts first (epoch 0). */
function createdAtMs(m: ChannelMessage): number {
  const t = Date.parse(m.created_at ?? '');
  return Number.isNaN(t) ? 0 : t;
}

/**
 * FIX-1c: merge the failed-outbox rows into the feed at render time.
 *
 * The failed rows do NOT live in the query cache (see failed-outbox.ts), so
 * consumers call this over the flattened feed before sorting/rendering:
 *
 *   mergeFailedIntoFeed(flattenMessagePages(data), useFailedMessages(channelId))
 *
 * Semantics:
 * - Dedupe by id: a failed row whose id already appears in `messages` is
 *   skipped (defensive — tmp ids should never come back from the server).
 *   Duplicate ids WITHIN `failed` are also collapsed (first wins).
 * - Surviving failed rows are appended at the END, sorted by `created_at`
 *   ascending, so they render by the composer where the send happened.
 * - Pure + identity-preserving: returns `messages` unchanged (same reference)
 *   when nothing needs merging.
 */
export function mergeFailedIntoFeed(
  messages: ChannelMessage[],
  failed: ChannelMessage[],
): ChannelMessage[] {
  if (failed.length === 0) return messages;
  const seen = new Set<string>();
  for (const m of messages) {
    if (m.id) seen.add(m.id);
  }
  const extras: ChannelMessage[] = [];
  for (const f of failed) {
    if (f.id) {
      if (seen.has(f.id)) continue;
      seen.add(f.id);
    }
    extras.push(f);
  }
  if (extras.length === 0) return messages;
  extras.sort((a, b) => createdAtMs(a) - createdAtMs(b));
  return [...messages, ...extras];
}
